<template>
  <ActionPanelShell title="Send an email" :subtitle="subtitle" @close="closeSlideOutPanel">
    <div class="sp" data-test="send-email-panel">
      <div v-if="initialLoading" class="sp-skel" aria-hidden="true" data-test="send-panel-loading"><i></i><i></i><i></i><i></i></div>

      <template v-else>
        <section class="sp-sec" data-test="send-email-compose">
          <PanelNotice v-if="apErrorText" tone="error" dismissible data-test="send-panel-error" @dismiss="apDismissError">{{ apErrorText }}</PanelNotice>
          <PanelNotice v-if="emailUnavailableReason" tone="warn" data-test="send-email-unavailable">{{ emailUnavailableReason }}</PanelNotice>

          <div class="sp-from" data-test="send-email-from">
            <i class="fa fa-envelope-o" aria-hidden="true"></i>
            <span>From {{ siteName }}<template v-if="fromEmail"> · {{ fromEmail }}</template></span>
          </div>

          <!-- Write your own: subject + message. A DESIGNED template replaces both with the card below,
               so nobody types into a box that gets ignored (it is sent by id with its own subject). -->
          <template v-if="!selectedEmailTemplate">
            <div>
              <label class="sp-label" for="send-email-subject">Subject</label>
              <input
                id="send-email-subject"
                v-model="emailSubject"
                type="text"
                class="sp-input"
                placeholder="Email subject"
                data-test="send-email-subject"
              />
            </div>

            <div class="sp-head">
              <span class="sp-h">Message</span>
              <span v-if="selectedWiki.length" class="sp-aside" data-test="send-email-from-template">
                From <b>{{ selectedWiki[0].Name }}</b>
                <button type="button" class="sp-link" data-test="send-email-clear-template" @click="clearEmailTemplateSelection">Clear</button>
              </span>
            </div>

            <MessageComposer :variables="emailInsertVariables" @insert="onInsert">
              <template #field>
                <textarea
                  ref="emailTextarea"
                  v-model="emailMessage"
                  rows="8"
                  placeholder="Write your email"
                  aria-label="Message"
                  data-test="send-email-message"
                ></textarea>
              </template>
              <template #status>
                <span data-test="send-email-vars-hint">{{ varsHint }}</span>
              </template>
            </MessageComposer>

            <PanelNotice v-if="Object.keys(templateLinkMap).length > 0" tone="info" icon="fa-link" data-test="send-email-links-note">
              Each [Form: …] becomes a link only {{ firstName }} can open when it sends. Items in curly braces are filled in automatically; you may delete them, but do not rename them.
            </PanelNotice>
            <!-- Decision 3A: text templates are emailed in English in v1 (no backend translation on this path). -->
            <PanelNotice v-if="selectedWiki.length > 0 && patientPrefersNonEnglish" tone="warn" data-test="send-email-english-note">
              This patient prefers a language other than English. Text templates are emailed in English.
            </PanelNotice>
          </template>

          <div v-else class="sp-card" data-test="send-email-designed">
            <div class="sp-card__head">
              <span class="sp-card__ic"><i class="fa fa-envelope" aria-hidden="true"></i></span>
              <div>
                <div class="sp-card__name" data-test="send-email-designed-name">{{ selectedEmailTemplate.Name }}</div>
                <div class="sp-card__meta" data-test="send-email-designed-subject">Subject: {{ designedSubject }}</div>
              </div>
            </div>
            <div class="sp-card__text">Sends exactly as designed in the Wiki, with its own subject. There’s nothing to write.</div>
            <button type="button" class="sp-link sp-link--start" data-test="send-email-clear-template" @click="clearEmailTemplateSelection">Write my own instead</button>
          </div>

          <!-- Send sits directly UNDER the message at every size (design 3a). -->
          <div class="sp-actions">
            <button
              type="button"
              class="sp-btn-primary"
              :disabled="sendDisabled"
              data-test="send-email-send"
              @click="onSend"
            ><i class="fa fa-paper-plane" aria-hidden="true"></i> {{ sendLabel }}</button>
            <button type="button" class="sp-btn-ghost" data-test="send-email-cancel" @click="closeSlideOutPanel">Cancel</button>
          </div>
          <p class="sp-note" data-test="send-email-credit-hint">
            <i class="fa fa-envelope-o" aria-hidden="true"></i> 1 email credit per email.
            <span v-if="emailCreditsBlocked" class="sp-note--bad" data-test="send-email-out-of-credits">Your practice is out of email credits.</span>
          </p>
        </section>

        <section class="sp-sec" data-test="send-email-templates">
          <div>
            <div class="sp-h">Templates</div>
            <p class="sp-sub">Designed emails send as they are. Text templates fill in the message so you can edit them.</p>
          </div>
          <PillTabs v-model="templateFilter" :items="filterTabs" aria-label="Which templates to list" />
          <TemplatePicker
            :key="templateFilter"
            :items="templateItems"
            :selected="selectedWiki.map(w => w.WikiPageID)"
            search-placeholder="Search templates"
            empty-text="No email or text templates yet. Add one in the Wiki."
            @toggle="onTemplateToggle"
          />
        </section>
      </template>
    </div>
  </ActionPanelShell>
</template>

<script>
/**
 * Send an email — the redesigned panel (design "Patient Action Panels" 2e, 3a).
 *
 * `extends` PatientSidebar: the email/SMS template loading and merge (mergeEmailPanelTemplates), the
 * greeting and subject prefill, selecting a template (selectEmailTemplate), the compose refusals and
 * the three send methods all run the classic code. Only the template and the styles are new — and
 * picking a DESIGNED email swaps the composer for an "as designed" card with its own subject.
 *
 * The failed-email fix lives in sendPanelCommon.js (sendEmailMsg throws on the store's `undefined`),
 * so a failure keeps this panel open with the error and never toasts "queued".
 *
 * ⛔ Keeps `ref="emailTextarea"` — PatientSidebar.insertEmailVariable() splices into it.
 */
import PatientSidebar from '@/pages/PatientProfile/components/PatientSidebar';
import ActionPanelShell from './ActionPanelShell.vue';
import MessageComposer from './MessageComposer.vue';
import TemplatePicker from './TemplatePicker.vue';
import PillTabs from './PillTabs.vue';
import PanelNotice from './PanelNotice.vue';
import sendPanelCommon from './sendPanelCommon.js';
import { pickVerifiedFromEmail } from '@/utils/verifiedSender.js';

export default {
  name: 'SendEmailPanel',

  extends: PatientSidebar,
  mixins: [sendPanelCommon],

  components: { ActionPanelShell, MessageComposer, TemplatePicker, PillTabs, PanelNotice },

  props: {
    // The host passes no sidebarType: this panel IS the Email one. (PatientSidebar requires it.)
    sidebarType: { type: String, default: 'Email', validator: v => v === 'Email' },
  },

  data() {
    return { templateFilter: 'all' };
  },

  computed: {
    subtitle() {
      const email = this.patientData && this.patientData.Email;
      return ['To ' + this.apPatientName, email].filter(Boolean).join(' · ');
    },
    siteName() {
      return (this.user && this.user.SiteName) || 'Your practice';
    },
    firstName() {
      return (this.patientData && this.patientData.FName) || 'the patient';
    },
    fromEmail() {
      return pickVerifiedFromEmail(this.allSettings && this.allSettings.GENERAL_VERIFIED_EMAILS);
    },
    varsHint() {
      return 'Variables like {{patient_first_name}} fill in when it sends.';
    },
    designedSubject() {
      const t = this.selectedEmailTemplate;
      return t ? (t.EmailSubject || t.Name) : '';
    },
    filterTabs() {
      return [
        { id: 'all', label: 'All' },
        { id: 'email', label: 'Designed emails' },
        { id: 'text', label: 'Text templates' },
      ];
    },
    templateItems() {
      const f = this.templateFilter;
      return (this.wikiPages || [])
        .filter(w => f === 'all' || (f === 'email' ? w.Kind === 'Email' : w.Kind === 'Text'))
        .map(w => ({
          id: w.WikiPageID,
          name: w.Name,
          desc: w.Kind === 'Email' ? `Subject: ${w.EmailSubject || w.Name}` : 'Copies into the message',
          badge: w.Kind,
          badgeTone: w.Kind === 'Email' ? 'blue' : 'slate',
        }));
    },
    sendLabel() {
      if (this.apBusy) return 'Sending…';
      return this.selectedEmailTemplate ? 'Send designed email' : 'Send email';
    },
    sendDisabled() {
      if (this.apBusy || this.emailUnavailableReason || this.emailCreditsBlocked) return true;
      if (this.selectedEmailTemplate) return false;
      return !(this.emailMessage || '').trim() || !(this.emailSubject || '').trim();
    },
  },

  methods: {
    onInsert(e) {
      if (e.kind === 'variable') this.insertEmailVariable(e.name);
    },

    // selectEmailTemplate is idempotent (picking the chosen row again is a no-op); clearing is explicit.
    onTemplateToggle(id) {
      const row = (this.wikiPages || []).find(w => w.WikiPageID === id);
      if (row) this.selectEmailTemplate(row);
    },

    onSend() {
      return this.selectedEmailTemplate ? this.sendEmailWithWiki() : this.sendComposedEmail();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './send-panel';

@include send-panel-base;

.sp-link--start { align-self: flex-start; }
</style>
